import { FunctionComponent } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { scrollIntoView } from 'seamless-scroll-polyfill';
import { isSafari } from 'utils/browserDetector';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  ScrollView,
  NavigationWrapper,
  FlippingCardContainer,
} from './HorizontalSlider.styles';
import { HorizontalSlideCard } from '../../Cards/HorizontalSlideCard';
import { Navigation } from '../../Navigation';
import { FlippingCard } from '../../Cards/FlippingCard';
import { CardBack } from '../../Cards/FlippingCard/CardBack';
import { CardFront } from '../../Cards/FlippingCard/CardFront';
import { Card } from '../../../models';
import useOverflow from '../../../../../../../hooks/useOverflow';

interface HorizontalSliderProps {
  cards: Card[];
  spaceBetween?: number;
  shouldFlip: boolean;
  ariaLabel?: string;
}

interface ScrollOptions extends ScrollIntoViewOptions {
  block: ScrollLogicalPosition;
  behavior?: ScrollBehavior;
}

const HorizontalSlider: FunctionComponent<HorizontalSliderProps> = ({
  cards,
  spaceBetween,
  shouldFlip,
  ariaLabel,
}) => {
  const { t } = useTranslation();

  const [cardHeight, setCardHeight] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDisablePrevious, setDisablePrevious] = useState(true);
  const [isDisableNext, setDisableNext] = useState(false);

  const allVisibleCardIds = useRef<string[]>([]);
  const scrollViewRef = useRef<HTMLDivElement | null>(null);
  const [isOverflow] = useOverflow(scrollViewRef);

  const consolidateVisibleItemIds = useCallback(
    (id: string, isVisible: boolean) => {
      allVisibleCardIds.current = allVisibleCardIds.current.filter(
        (visibleId) => id !== visibleId,
      );
      if (isVisible) {
        allVisibleCardIds.current = [...allVisibleCardIds.current, id];
      }
      setDisablePrevious(
        allVisibleCardIds.current.some((id) => id === `pp_carousel_item_0`),
      );
      setDisableNext(
        allVisibleCardIds.current.some(
          (id) => id === `pp_carousel_item_${cards.length - 1}`,
        ),
      );
    },
    [allVisibleCardIds, cards.length],
  );

  const onPrevious = useCallback(() => {
    const numberOfItemsPerView = allVisibleCardIds.current.length;
    const idItemEndLeft = allVisibleCardIds.current
      .sort()[0]
      .split('_')
      .slice(-1);
    const nextId = +idItemEndLeft - numberOfItemsPerView;
    setCurrentIndex(nextId < 0 ? 0 : nextId);
  }, [allVisibleCardIds]);

  const onNext = useCallback(() => {
    const numberOfItemsPerView = allVisibleCardIds.current.length;
    const idItemEndRight = allVisibleCardIds.current
      .sort()
      .slice(-1)[0]
      .split('_')
      .slice(-1);
    const nextId = +idItemEndRight + numberOfItemsPerView;
    setCurrentIndex(nextId >= cards.length ? cards.length - 1 : nextId);
  }, [allVisibleCardIds, cards.length]);

  useEffect(() => {
    if (cards[currentIndex]) {
      const card = document.getElementById(cards[currentIndex].id);
      if (!card) return;

      const commonsOptions: ScrollOptions = {
        block: 'nearest',
        behavior: 'smooth',
      };

      if (isSafari()) {
        scrollIntoView(card, {
          ...commonsOptions,
          inline: 'center',
        });
        return;
      }
      card.scrollIntoView({ ...commonsOptions, inline: 'end' });
    }
  }, [currentIndex, cards]);

  return (
    <div>
      <ScrollView
        id="scroll_view"
        ref={scrollViewRef}
        centerItems={!isOverflow}
        role="region"
        aria-label={ariaLabel}
      >
        <Stack height={cardHeight} gap={spaceBetween} align="center">
          {cards.map((card, index) => (
            <HorizontalSlideCard
              key={card.id}
              index={index}
              cardHeight={cardHeight}
              setCardHeight={setCardHeight}
              consolidateVisibleItemIds={consolidateVisibleItemIds}
              aria-hidden={
                !allVisibleCardIds.current.includes(`pp_carousel_item_${index}`)
              }
            >
              {shouldFlip ? (
                <FlippingCard
                  card={card.data}
                  cardFront={CardFront}
                  cardBack={CardBack}
                  flippingCardContainer={FlippingCardContainer}
                />
              ) : (
                <CardFront card={card.data} />
              )}
            </HorizontalSlideCard>
          ))}
        </Stack>
      </ScrollView>

      {isOverflow && (
        <NavigationWrapper aria-hidden={!isOverflow}>
          <Navigation
            onNext={onNext}
            onPrevious={onPrevious}
            isDisableNext={isDisableNext}
            isDisablePrevious={isDisablePrevious}
            aria-controls="scroll_view"
            aria-label={t('ALT_TEXT_SLIDER_NAVIGATION')}
          />
        </NavigationWrapper>
      )}
    </div>
  );
};

HorizontalSlider.defaultProps = {
  cards: [],
  spaceBetween: 0,
  ariaLabel: '',
};

export { HorizontalSlider };
